// frontend: src/pages/ManageQuestions.jsx  (replace your file)
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../api/api";
import Layout from "../components/Layout";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

export default function ManageQuestions() {
  const { interviewId, roundId } = useParams();
  const navigate = useNavigate();

  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editId, setEditId] = useState(null);

  const [form, setForm] = useState({
    questionText: "",
    type: "Technical",
    difficulty: "Easy",
    notes: "",
    attachment: null,
    isImportant: false,
  });

  // --- COPILOT states ---
  const DOMAINS = {
    programming: ["Java", "Node.js", "React", "Python"],
    devops: ["Docker", "Kubernetes", "Jenkins", "Terraform"],
    cloud: ["AWS", "Azure", "GCP"],
    qa: ["Manual Testing", "Automation"],
    data: ["SQL", "Python", "Tableau"]
  };

  const [copilotDomain, setCopilotDomain] = useState("programming");
  const [copilotSub, setCopilotSub] = useState(DOMAINS.programming[0]);
  const [copilotCount, setCopilotCount] = useState(5);
  const [copilotOpen, setCopilotOpen] = useState(false);
  const [copilotLoading, setCopilotLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  // -----------------------

  useEffect(() => {
    const fetchQuestions = async () => {
      try {
        const res = await api.get(`/rounds/${roundId}/questions`);
        setQuestions(res.data || []);
      } catch (err) {
        console.error("Failed to load questions:", err);
        setError("Failed to load questions.");
      } finally {
        setLoading(false);
      }
    };

    fetchQuestions();
  }, [roundId]);

  const handleChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    if (type === "checkbox") {
      setForm((prev) => ({ ...prev, [name]: checked }));
    } else if (type === "file") {
      setForm((prev) => ({ ...prev, attachment: files[0] }));
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.questionText || !form.questionText.toString().trim()) return;
    setSubmitting(true);

    try {
      if (isEditing) {
        const res = await api.put(`/rounds/${roundId}/questions/${editId}`, {
          questionText: form.questionText,
          type: form.type,
          difficulty: form.difficulty,
          notes: form.notes,
          isImportant: form.isImportant,
        });

        setQuestions((prev) =>
          prev.map((q) => (q.id === editId ? res.data.question : q))
        );
        toast.success("Question updated successfully!");
      } else {
        const formData = new FormData();
        formData.append("questionText", form.questionText);
        formData.append("type", form.type);
        formData.append("difficulty", form.difficulty);
        formData.append("notes", form.notes);
        formData.append("isImportant", form.isImportant);
        if (form.attachment) formData.append("attachment", form.attachment);

        const res = await api.post(`/rounds/${roundId}/questions`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });

        setQuestions((prev) => [...prev, res.data]);
        toast.success("Question added successfully!");
      }

      setForm({
        questionText: "",
        type: "Technical",
        difficulty: "Easy",
        notes: "",
        attachment: null,
        isImportant: false,
      });
      setIsEditing(false);
      setEditId(null);
    } catch (err) {
      console.error("Submit error:", err.response?.data || err.message);
      toast.error("Failed to submit question.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (q) => {
    setForm({
      questionText: q.questionText,
      type: q.type,
      difficulty: q.difficulty,
      notes: q.notes || "",
      attachment: null,
      isImportant: q.isImportant || false,
    });
    setIsEditing(true);
    setEditId(q.id);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDelete = async (questionId) => {
    const confirm = window.confirm("Are you sure you want to delete this question?");
    if (!confirm) return;

    try {
      await api.delete(`/rounds/${roundId}/questions/${questionId}`);
      setQuestions((prev) => prev.filter((q) => q.id !== questionId));
      toast.success("Question deleted successfully!");
    } catch (err) {
      console.error("Delete error:", err.response?.data || err.message);
      toast.error("Failed to delete question.");
    }
  };

  const handleFinishRound = () => {
    const confirmFinish = window.confirm("Are you sure you're done adding questions?");
    if (confirmFinish) {
      navigate(`/interviews/${interviewId}`);
    }
  };

  const quillModules = {
    toolbar: [
      [{ header: [1, 2, false] }],
      ["bold", "italic", "underline"],
      [{ list: "ordered" }, { list: "bullet" }],
      ["link"],
    ],
  };

  // ---------- Copilot functions ----------
  const askCopilot = async () => {
    try {
      setCopilotLoading(true);
      setCopilotOpen(true);
      setSuggestions([]);
      const payload = {
        domain: copilotDomain,
        subcategory: copilotSub,
        count: copilotCount
      };
      const res = await api.post('/copilot/questions', payload);
      setSuggestions(res.data.suggestions || []);
    } catch (err) {
      console.error('Copilot error:', err?.response?.data || err?.message);
      toast.error(err?.response?.data?.message || "Copilot failed");
      setSuggestions([]);
    } finally {
      setCopilotLoading(false);
    }
  };
// rename function
const handleSuggestion = (text) => {
  setForm((prev) => ({ ...prev, questionText: text }));
  setCopilotOpen(false);
  window.scrollTo({ top: 0, behavior: "smooth" });
};

  
  // ---------------------------------------

  return (
    <Layout>
      <h2 className="text-2xl font-bold mb-4">Manage Questions</h2>

      {error && <p className="text-red-500">{error}</p>}

      {loading ? (
        <p>Loading questions...</p>
      ) : (
        <>
          <ul className="space-y-2 mb-6">
            {questions.map((q, i) => (
              <li key={q.id || i} className="p-4 border bg-gray-50 rounded relative">
                <p>
                  <strong>Q:</strong>{" "}
                  <span dangerouslySetInnerHTML={{ __html: q.questionText }} />
                </p>
                <p><em>Type:</em> {q.type} | <em>Difficulty:</em> {q.difficulty}</p>
                {q.notes && (
                  <p>
                    <em>Notes:</em>{" "}
                    <span dangerouslySetInnerHTML={{ __html: q.notes }} />
                  </p>
                )}
                {q.isImportant && <p className="text-red-600 font-semibold">★ Marked Important</p>}

                <div className="absolute right-2 top-2 flex space-x-2 text-sm">
                  <button onClick={() => handleEdit(q)} className="text-blue-600 hover:underline">
                    Edit
                  </button>
                  <button onClick={() => handleDelete(q.id)} className="text-red-600 hover:underline">
                    Delete
                  </button>
                </div>
              </li>
            ))}
            {questions.length === 0 && (
              <li className="text-gray-500">No questions added yet.</li>
            )}
          </ul>

          {/* FORM */}
          <form onSubmit={handleSubmit} className="space-y-4 border-t pt-4">
            {/* Copilot controls (small, non-intrusive) */}
            <div className="flex items-center space-x-3 mb-2">
              <select
                value={copilotDomain}
                onChange={(e) => {
                  setCopilotDomain(e.target.value);
                  setCopilotSub(DOMAINS[e.target.value][0]);
                }}
                className="border px-2 py-1 rounded"
              >
                {Object.keys(DOMAINS).map(k => (
                  <option key={k} value={k}>{k}</option>
                ))}
              </select>

              <select
                value={copilotSub}
                onChange={(e) => setCopilotSub(e.target.value)}
                className="border px-2 py-1 rounded"
              >
                {(DOMAINS[copilotDomain] || []).map(s => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>

              <input
                type="number"
                value={copilotCount}
                min={1}
                max={20}
                onChange={(e) => setCopilotCount(Number(e.target.value))}
                className="w-16 border px-2 py-1 rounded"
              />

              <button
                type="button"
                onClick={askCopilot}
                className="bg-yellow-400 px-3 py-1 rounded hover:bg-yellow-500"
              >
                ✨ Ask Copilot
              </button>

              <div className="text-sm text-gray-500">Suggestions fill editor for edit → Save</div>
            </div>

            {/* Rich Text Editor for Question */}
            <div>
              <label className="font-medium mb-1 block">Question:</label>
              <ReactQuill
                theme="snow"
                value={form.questionText}
                onChange={(value) => setForm((prev) => ({ ...prev, questionText: value }))}
                modules={quillModules}
                placeholder="Enter your question here..."
              />
            </div>

            <div className="flex space-x-4">
              <select
                name="type"
                value={form.type}
                onChange={handleChange}
                className="border px-3 py-2 rounded"
              >
                <option>Technical</option>
                <option>HR</option>
                <option>Behavioral</option>
              </select>

              <div className="flex items-center space-x-2">
                <label className="font-medium">Difficulty:</label>
                <button
                  type="button"
                  className={`px-3 py-1 rounded ${form.difficulty === "Easy" ? "bg-blue-500 text-white" : "bg-gray-200"}`}
                  onClick={() => setForm({ ...form, difficulty: "Easy" })}
                >
                  Easy
                </button>
                <button
                  type="button"
                  className={`px-3 py-1 rounded ${form.difficulty === "Hard" ? "bg-red-500 text-white" : "bg-gray-200"}`}
                  onClick={() => setForm({ ...form, difficulty: "Hard" })}
                >
                  Hard
                </button>
              </div>
            </div>

            <div>
              <label className="font-medium mb-1 block">Notes / Expected Answer:</label>
              <ReactQuill
                theme="snow"
                value={form.notes}
                onChange={(value) => setForm((prev) => ({ ...prev, notes: value }))}
                modules={quillModules}
                placeholder="Optional notes or expected answer"
              />
            </div>

            <input
              type="file"
              name="attachment"
              onChange={handleChange}
              className="block"
            />

            <label className="inline-flex items-center">
              <input
                type="checkbox"
                name="isImportant"
                checked={form.isImportant}
                onChange={handleChange}
                className="mr-2"
              />
              Mark as important
            </label>

            <div className="flex space-x-4">
              <button
                type="submit"
                disabled={submitting}
                className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
              >
                {submitting ? (isEditing ? "Updating..." : "Adding...") : isEditing ? "Update Question" : "Add Question"}
              </button>

              <button
                type="button"
                onClick={handleFinishRound}
                className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
              >
                ✅ Finish Round
              </button>
            </div>
          </form>

          {/* Copilot modal */}
          {copilotOpen && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
              <div className="bg-white rounded-lg w-full max-w-2xl p-6 overflow-auto max-h-[80vh]">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-bold">Copilot suggestions ({copilotDomain} / {copilotSub})</h3>
                  <div>
                    <button onClick={() => setCopilotOpen(false)} className="px-3 py-1 bg-gray-200 rounded">Close</button>
                  </div>
                </div>

                {copilotLoading ? (
                  <p>Generating suggestions…</p>
                ) : suggestions.length === 0 ? (
                  <p className="text-gray-500">No suggestions returned.</p>
                ) : (
                  <ul className="space-y-3">
                    {suggestions.map((s, idx) => (
                      <li key={idx} className="p-3 border rounded bg-gray-50">
                        <div className="mb-2">
                          <div dangerouslySetInnerHTML={{ __html: s }} />
                        </div>
                        <div className="flex space-x-2">
                          <button onClick={() => handleSuggestion(s)} className="px-3 py-1 bg-indigo-600 text-white rounded">Use</button>
                          <button onClick={() => { navigator.clipboard?.writeText(s); toast.info('Copied'); }} className="px-3 py-1 bg-gray-200 rounded">Copy</button>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </Layout>
  );
}
